import { ModalMessage } from '@/components/element/modal/ModalMessage';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { PurchaseOrderFiltersModal } from '@/features/w/[identifier]/teams/[team_id]/purchase_orders/{components}/PurchaseOrderFilterModal';
import { PurchaseOrderSearchConditionTags } from '@/features/w/[identifier]/teams/[team_id]/purchase_orders/{components}/PurchaseOrderSearchConditionTags';
import { PurchaseOrdersListElement } from '@/features/w/[identifier]/teams/[team_id]/purchase_orders/{components}/PurchaseOrdersListElement';
import { useTeamPurchaseOrderPhases } from '@/hooks/api/0260_team_purchase_order_phases';
import { usePurchaseOrders, usePurchaseOrdersInvalidate } from '@/hooks/api/0520_purchase_orders';
import { purchaseOrderInvalidate } from '@/hooks/api/0570_purchase_order';
import { useUpdatePurchaseOrderPhase } from '@/hooks/api/0580_update_purchase_order_phase';
import { PurchaseOrder, Team, TeamPurchaseOrderPhase } from '@/hooks/api/models';
import { TeamMemberLogic } from '@/hooks/api/models/logics';
import { useWindowSize } from '@/hooks/useWindowSize';
import { toastError } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useNavState } from '@/providers/NavStateProvider';
import { useSearchCondition } from '@/providers/SearchConditionProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { dateFormat } from '@/utils';
import {
  Active,
  closestCenter,
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  getFirstCollision,
  MeasuringStrategy,
  MouseSensor,
  pointerWithin,
  rectIntersection,
  UniqueIdentifier,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useIntercomGuide } from '@/hooks/useIntercomGuide';

export const TeamPurchaseOrders = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  const { _w_, _t_, me, meTeamMember, isTeamAdmin, isApprover, isFinancialOfficer, isBackoffice, isMember } =
    useCurrentTeamMember();

  const {
    purchaseOrderDisplayType: displayType,
    setPurchaseOrderDisplayType: setDisplayType,
    setPurchaseOrderSearchCondition: setSearchCondition,
  } = useSearchCondition();
  const { invalidate } = usePurchaseOrdersInvalidate(_w_!.identifier, _t_!.id);
  const [curTeam, setCurTeam] = useState<Team>();

  useEffect(() => setTitle(`プロジェクト（費用） - ${curTeam?.name}`), [curTeam]);
  useEffect(() => setBreadcrumbs([]), []);

  useIntercomGuide();

  useEffect(() => {
    if (_t_! != curTeam) {
      // 1ページ目に戻す
      setSearchCondition((state) => ({ ...state, page: 1 }));
      setCurTeam(_t_!);
    }
  }, [_t_, displayType]);

  // チームを切り替えた際にSearchConditionの切り替え前にレンダリングされるのを防ぐため
  if (_t_! != curTeam) return <></>;

  return (
    <WorkspaceLayout>
      <div className="container-fluid py-4">
        <div className="d-flex mb-4 align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div className="btn-group">
              <button
                className={`btn btn-icon ${displayType == 'list' ? 'btn-dark' : 'btn-outline-dark'}`}
                onClick={() => setDisplayType('list')}
              >
                <i className="fas fa-th-list"></i>リスト
              </button>
              <button
                className={`btn btn-icon ${displayType == 'kanban' ? 'btn-dark' : 'btn-outline-dark'}`}
                onClick={() => setDisplayType('kanban')}
              >
                <i className="fas fa-th-large"></i>ボード
              </button>
            </div>
            <button className="btn btn-icon-only btn-dark mx-2" data-bs-toggle="modal" data-bs-target="#modal-filter">
              <i className="fas fa-filter"></i>
            </button>
          </div>
          <div className="ms-auto d-flex justify-content-end align-items-center">
            <div className="pe-4 position-relative">
              <p className="text-secondary text-xs font-weight-bold mb-2">チームメンバー</p>
              <div className="d-flex align-items-center justify-content-center">
                <div className="avatar-group text-nowrap overflow-hidden flex-1">
                  {_t_!.team_members.map((tm) => (
                    <img
                      key={`tm-icon-${tm.id}`}
                      src={tm.user.user_account.icon_file_url}
                      className="avatar avatar-xs rounded-circle"
                    />
                  ))}
                </div>
              </div>
              <hr className="vertical dark mt-0" />
            </div>
            <div className="ps-4">
              {TeamMemberLogic.canCreatePurchaseOrder(_t_!, meTeamMember!) && (
                <Link
                  to={`/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/new`}
                  className="btn bg-gradient-primary btn-icon mb-0"
                  data-toggle="modal"
                  data-target="#new-board-modal"
                >
                  <i className="fa fa-plus"></i> 新規プロジェクト（費用）作成
                </Link>
              )}
            </div>
          </div>
        </div>

        <PurchaseOrderSearchConditionTags />

        <PurchaseOrderFiltersModal id="modal-filter" />

        {displayType == 'list' && <PurchaseOrdersListElement />}
        {displayType == 'kanban' && <PurchaseOrdersKanbanElement />}
      </div>
    </WorkspaceLayout>
  );
};

type KanbanBoardType = {
  id: UniqueIdentifier;
  data: TeamPurchaseOrderPhase;
  title: string;
  items: KanbanItemType[];
  activeId?: UniqueIdentifier | null;
};

type KanbanItemType = {
  id: UniqueIdentifier;
  data: PurchaseOrder;
  title: string;
  activeId?: UniqueIdentifier | null;
};

const PurchaseOrdersKanbanElement = () => {
  const { _w_, _t_, me, meTeamMember, isTeamAdmin, isApprover, isFinancialOfficer, isBackoffice, isMember } =
    useCurrentTeamMember();
  const { data: phases } = useTeamPurchaseOrderPhases({ identifier: _w_!.identifier, team_id: _t_!.id });
  if (phases) return <_PurchaseOrdersKanbanElement phases={phases} />;
  else return <></>;
};

const _PurchaseOrdersKanbanElement = ({ phases }: { phases: TeamPurchaseOrderPhase[] }) => {
  const { _w_, _t_, me, meTeamMember, isTeamAdmin, isApprover, isFinancialOfficer, isBackoffice, isMember } =
    useCurrentTeamMember();
  const { setShowLoading } = useLoading();
  const { purchaseOrderSearchCondition: searchCondition, setPurchaseOrderSearchCondition: setSearchCondition } =
    useSearchCondition();
  const [allLoaded, setAllLoaded] = useState(false);
  const baseSearchCondition = {
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    per: 1000,
    ...searchCondition,
    page: 1,
  };
  const ordersPhase1SearchCondition = {
    ...baseSearchCondition,
    enabled: false,
    team_purchase_order_phase_ids: phases ? [phases[0].id] : [],
  };
  const ordersPhase2SearchCondition = {
    ...baseSearchCondition,
    enabled: false,
    team_purchase_order_phase_ids: phases ? [phases[1].id] : [],
  };
  const ordersPhase3SearchCondition = {
    ...baseSearchCondition,
    enabled: false,
    team_purchase_order_phase_ids: phases ? [phases[2].id] : [],
  };
  const ordersPhase4SearchCondition = {
    ...baseSearchCondition,
    enabled: false,
    team_purchase_order_phase_ids: phases ? [phases[3].id] : [],
  };

  const { data: ordersPhase1Resp, refetch: ordersPhase1Refetch } = usePurchaseOrders(ordersPhase1SearchCondition);
  const { data: ordersPhase2Resp, refetch: ordersPhase2Refetch } = usePurchaseOrders(ordersPhase2SearchCondition);
  const { data: ordersPhase3Resp, refetch: ordersPhase3Refetch } = usePurchaseOrders(ordersPhase3SearchCondition);
  const { data: ordersPhase4Resp, refetch: ordersPhase4Refetch } = usePurchaseOrders(ordersPhase4SearchCondition);

  const [boards, setBoards] = useState<KanbanBoardType[]>(
    phases.map((phase) => ({
      id: `b-${phase.id}`,
      data: phase,
      title: phase.label,
      items: [] as KanbanItemType[],
    }))
  );

  useEffect(() => setShowLoading(!allLoaded), [allLoaded]);

  const fetchAll = () => {
    setAllLoaded(false);

    ordersPhase1Refetch();
    ordersPhase2Refetch();
    ordersPhase3Refetch();
    ordersPhase4Refetch();
  };

  useEffect(() => {
    fetchAll();
  }, [searchCondition]);

  useEffect(() => {
    if (ordersPhase1Resp && ordersPhase2Resp && ordersPhase3Resp && ordersPhase4Resp) {
      const responses = [ordersPhase1Resp, ordersPhase2Resp, ordersPhase3Resp, ordersPhase4Resp];
      setBoards((prevState) =>
        prevState.map((b, idx) => {
          b.items = responses[idx].purchase_orders.map((po) => ({
            id: `i-${po.id}`,
            data: po,
            title: po.name,
          }));
          return b;
        })
      );

      setAllLoaded(true);
    }
  }, [ordersPhase1Resp, ordersPhase2Resp, ordersPhase3Resp, ordersPhase4Resp]);

  if (!allLoaded) return <></>;

  return <KanbanContainer boards={boards} setBoards={setBoards} />;
};

const KanbanContainer = ({
  boards,
  setBoards,
}: {
  boards: KanbanBoardType[];
  setBoards: Dispatch<SetStateAction<KanbanBoardType[]>>;
}) => {
  const { _w_, _t_, me, meTeamMember, isTeamAdmin, isApprover, isFinancialOfficer, isBackoffice, isMember } =
    useCurrentTeamMember();
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const { mutate, data, error } = useUpdatePurchaseOrderPhase();

  const lastOverId = useRef<UniqueIdentifier | null>(null);
  const recentlyMovedToNewContainer = useRef(false);
  const [width, height] = useWindowSize();
  const kanbanContainerRef = useRef<HTMLDivElement>(null);
  const showMessageButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (kanbanContainerRef.current) {
      const cTop = kanbanContainerRef.current.offsetTop;
      kanbanContainerRef.current.style.height = `${height - cTop - 80}px`;
    }
  }, [kanbanContainerRef, width, height]);

  useEffect(() => {
    if (data) {
      purchaseOrderInvalidate({ identifier: _w_!.identifier, team_id: _t_!.id, id: data.id });

      if ('契約未承認' == data.alert) {
        // フェーズ移動でアラートが設定された場合にモーダルでメッセージ表示
        showMessageButtonRef.current?.click();
      }

      // 更新されたプロジェクトの差し替え
      setBoards((prevState) =>
        prevState.map((b) => {
          b.items.map((item) => {
            if (item.data.id == data.id) {
              item.data = data;
            }
            return item;
          });
          return b;
        })
      );
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      const msgs: string[] = (error as any).response.data.errors || [];
      if (msgs.length > 0) toastError(msgs[0]);
    }
  }, [error]);

  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 5 } }));

  const findBoard = useCallback(
    (unique: string | null) => {
      if (!unique) {
        return null;
      }
      // overの対象がcolumnの場合があるためそのままidを返す
      if (boards.some((b) => b.id === unique)) {
        return boards.find((b) => b.id === unique) ?? null;
      }
      const id = String(unique);
      const itemWithColumnId = boards.flatMap((b) => {
        const boardId = b.id;
        return b.items.map((i) => ({ itemId: i.id, boardId: boardId }));
      });
      const columnId = itemWithColumnId.find((i) => i.itemId === id)?.boardId;
      return boards.find((b) => b.id === columnId) ?? null;
    },
    [boards]
  );

  const findItem = useCallback(
    (id: UniqueIdentifier): KanbanItemType | undefined => {
      let res: KanbanItemType | undefined;
      boards.forEach((b) => {
        if (res != null) return;

        const _i = b.items.find((i) => i.id == id);
        if (_i) res = _i;
      });
      return res;
    },
    [boards]
  );

  // ドラッグ開始時に発火する関数
  const handleDragStart = useCallback(
    ({ active }: { active: Active }) => {
      setActiveId(active.id);
    },
    [boards]
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over, delta } = event;
      const activeId = String(active.id);
      const overId = over ? String(over.id) : null;
      const activeColumn = findBoard(activeId);
      const overColumn = findBoard(overId);
      if (!activeColumn || !overColumn || activeColumn === overColumn) {
        return null;
      }
      setBoards((prevState) => {
        const activeItems = activeColumn.items;
        const overItems = overColumn.items;
        const activeIndex = activeItems.findIndex((i) => i.id === activeId);
        const overIndex = overItems.findIndex((i) => i.id === overId);

        recentlyMovedToNewContainer.current = true;

        const newIndex = () => {
          const putOnBelowLastItem = overIndex === overItems.length - 1 && delta.y > 0;
          const modifier = putOnBelowLastItem ? 1 : 0;
          return overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
        };
        return prevState.map((c) => {
          if (c.id === activeColumn.id) {
            c.items = activeItems.filter((i) => i.id !== activeId);
            return c;
          } else if (c.id === overColumn.id) {
            c.items = [
              ...overItems.slice(0, newIndex()),
              activeItems[activeIndex],
              ...overItems.slice(newIndex(), overItems.length),
            ];
            return c;
          } else {
            return c;
          }
        });
      });
    },
    [boards]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      const activeId = String(active.id);
      const overId = over ? String(over.id) : null;

      setActiveId(null);

      const activeColumn = findBoard(activeId);
      const overColumn = findBoard(overId);
      if (!activeColumn || !overColumn || activeColumn !== overColumn) {
        return null;
      }
      const activeIndex = activeColumn.items.findIndex((i) => i.id === activeId);
      const overIndex = overColumn.items.findIndex((i) => i.id === overId);
      if (activeIndex !== overIndex) {
        setBoards((prevState) => {
          return prevState.map((column) => {
            if (column.id === activeColumn.id) {
              column.items = arrayMove(overColumn.items, activeIndex, overIndex);
              return column;
            } else {
              return column;
            }
          });
        });
      }

      const movedItem = findItem(activeId);
      if (movedItem) {
        if (movedItem.data.team_purchase_order_phase.id != overColumn.data.id) {
          mutate({
            identifier: _w_!.identifier,
            team_id: _t_!.id,
            purchase_order_id: movedItem.data.id,
            team_purchase_order_phase_id: overColumn.data.id,
          });
        }
      }
    },
    [boards]
  );

  const handleDragCancel = () => {
    setActiveId(null);
    useEffect(() => {
      requestAnimationFrame(() => (recentlyMovedToNewContainer.current = false));
    }, [boards]);
  };

  const collisionDetectionStrategy: CollisionDetection = useCallback(
    (args) => {
      if (activeId && activeId in boards) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((container) => container.id in boards),
        });
      }

      // Start by finding any intersecting droppable
      const pointerIntersections = pointerWithin(args);
      const intersections =
        pointerIntersections.length > 0
          ? // If there are droppables intersecting with the pointer, return those
            pointerIntersections
          : rectIntersection(args);
      let overId = getFirstCollision(intersections, 'id');

      if (overId != null) {
        if (boards.some((b) => b.id == overId)) {
          const containerItems = boards.find((b) => b.id == overId)?.items;

          if (containerItems && containerItems.length > 0) {
            overId = closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                (container) => container.id !== overId && containerItems.some((i) => i.id == container.id)
              ),
            })[0]?.id;
          }
        }

        return [{ id: overId }];
      }

      // When a draggable item moves to a new container, the layout may shift
      // and the `overId` may become `null`. We manually set the cached `lastOverId`
      // to the id of the draggable item that was moved to the new container, otherwise
      // the previous `overId` will be returned which can cause items to incorrectly shift positions
      if (recentlyMovedToNewContainer.current) {
        lastOverId.current = activeId;
      }

      // If no droppable is matched, return the last match
      return lastOverId.current ? [{ id: lastOverId.current }] : [];
    },
    [activeId, boards]
  );

  return (
    <>
      <ModalMessage
        modalId="messageModal"
        message="承認された契約申請（費用）がありません"
        showModalButtonRef={showMessageButtonRef}
      />

      <DndContext
        sensors={sensors}
        // collisionDetection={closestCorners}
        collisionDetection={collisionDetectionStrategy}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.Always,
          },
        }}
      >
        <div id="myKanban" className="mb-4" style={{ overflowX: 'auto' }}>
          <div className="kanban-container" style={{ width: 1240 }} ref={kanbanContainerRef}>
            {boards.map((b) => {
              return <KanbanBoard key={`board-${b.id}`} {...b} activeId={activeId} />;
            })}
          </div>
          <DragOverlay>{activeId ? <OverlayKanbanItem {...findItem(activeId)!} /> : null}</DragOverlay>
        </div>
      </DndContext>
    </>
  );
};

const DraggableKanbanItem = ({ id, activeId, ...props }: KanbanItemType) => {
  const navigate = useNavigate();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      className="kanban-item border-radius-md"
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={id == activeId ? { ...style, opacity: 0.4 } : style}
      onClick={() => navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${props.data.id}`)}
    >
      <KanbanItemElement id={id} title={props.title} data={props.data} />
    </div>
  );
};

const OverlayKanbanItem = (props: KanbanItemType) => {
  return (
    <div className="kanban-item border-radius-md" style={{ transform: 'rotate(2deg)', opacity: 0.8 }}>
      <KanbanItemElement {...props} />
    </div>
  );
};

const KanbanItemElement = ({ title, data }: KanbanItemType) => {
  const { _w_, _t_ } = useCurrentTeamMember();

  return (
    <>
      {data.alert && (
        <div className="mb-2">
          <span className="badge badge-xs badge-danger">{data.alert}</span>
        </div>
      )}
      <div className="mb-1">
        <span className="badge badge-xs bg-gradient-primary me-1 text-truncate">
          {data.business_partner.display_name}
        </span>
      </div>
      <h6 className="text-truncate text-sm mb-0">
        <Link to={`/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${data.id}`}>{title}</Link>
      </h6>
      <div className="avatar-group mb-2">
        <img src={data.user.user_account.icon_file_url} className="avatar avatar-xs rounded-circle me-1" />
        <span className="text-xs text-dark text-truncate">{data.user.user_account.name}</span>
        {/*{data.purchase_order_members.map((m) => (*/}
        {/*  <img key={`kanban-item-member-img-${m.id}`} src={m.user.user_account.icon_file_url} className="avatar avatar-xs rounded-circle me-1" />*/}
        {/*))}*/}
      </div>
      <div className="d-flex flex-wrap">
        <span className="badge badge-xs bg-gradient-info me-1 mb-1 text-truncate">{data.payment_context.label}</span>
        <span className="badge badge-xs bg-gradient-success me-1 mb-1 text-truncate">
          ¥{data.accounting_total_amount.toLocaleString()}
        </span>
        <span className="badge badge-xs bg-gradient-secondary me-1 mb-1 text-truncate">
          {data.payment_method.label}
        </span>
      </div>
      <hr className="horizontal dark my-2" />
      <div className="d-flex justify-content-between">
        <div>
          <div className="font-weight-bold text-dark text-xs">{data.purchase_order_comments_count}</div>
          <div className="text-secondary text-xxs">コメント</div>
        </div>
        <div className="text-end">
          <div className="font-weight-bold text-dark text-xs">{dateFormat({ date: data.updated_at })}</div>
          <div className="text-secondary text-xxs">最終更新日</div>
        </div>
      </div>
    </>
  );
};

const KanbanBoard = ({ id, title, items, activeId }: KanbanBoardType) => {
  const { setNodeRef } = useDroppable({ id });

  return (
    <SortableContext items={items} strategy={rectSortingStrategy}>
      <div className="kanban-board" style={{ width: 300, marginLeft: 10, marginTop: 10 }}>
        <header className="kanban-board-header">
          <div className="kanban-title-board">{title}</div>
        </header>
        <div ref={setNodeRef} className="kanban-drag" style={{ height: '100%' }}>
          {items.map((i) => {
            return <DraggableKanbanItem key={`kanban-${i.data.id}`} {...i} activeId={activeId} />;
          })}
        </div>
      </div>
    </SortableContext>
  );
};
